import { useState, useEffect, forwardRef, useImperativeHandle, RefObject } from "react";
import { Button, DeprecatedTooltip, TextSwitch, Theme } from "@landtechnologies/components";

import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { MeasurementSystem } from "src/js/stores/user/store";
import { logEvent, unitOfMeasurement } from "react-migration/lib";
import {
  startComparablesDrawingTool,
  getSelectedGeometry,
  deleteDrawing,
  setUnitPreference,
  setUnitSize,
  updatedSelectedGeometry,
} from "src/js/stores/drawing/actions";

import { transformScale, AllGeoJSON } from "src/js/util/turf";

import { polygonRadius } from "react-migration/domains/comparables/util";
import { RadiusInput, Tool } from "../../DrawingTools";
import { DrawingItem, PriceModes, Shape } from "../../../typings";
import { ComparablesCalculatorErrorMessage } from "../ComparablesCalculatorErrorMessage/ComparablesCalculatorErrorMessage";

interface ClearDrawingRef {
  clearDrawingOnClose: () => void | null;
}

export interface SelectAreaProps {
  drawing?: DrawingItem;
  isTooLarge?: boolean;
  ref: RefObject<ClearDrawingRef>;
  priceMode: PriceModes;
  setDrawing: (v: DrawingItem | undefined) => void;
  setSmallUnitSize: (smallUnitSize: boolean) => void;
  setStartDrawing: (v: boolean | undefined) => void;
  smallUnitSize: boolean;
  theme?: Theme;
  unitPreference: MeasurementSystem;
}

// eslint-disable-next-line react/display-name
export const SelectArea = forwardRef<ClearDrawingRef, SelectAreaProps>(
  (
    {
      drawing,
      isTooLarge,
      priceMode,
      setDrawing,
      setSmallUnitSize,
      setStartDrawing,
      smallUnitSize,
      theme,
      unitPreference,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [selectedTool, setSelectedTool] = useState<string>("");
    useEffect(() => {
      setUnitPreference(unitPreference);
      !smallUnitSize && setUnitSize("large");
    }, [smallUnitSize, unitPreference]);

    useImperativeHandle(ref, () => ({
      clearDrawingOnClose() {
        clearDrawing();
      },
    }));

    const handleSelectedToolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const tool = e.target.value as Shape;
      setStartDrawing(true);
      // Clears the radius input when the user switches to a different tool
      tool == "ComparablesPolygon" && setDrawing(undefined);
      setSelectedTool(tool);
      startComparablesDrawingTool(tool);
      logEvent("Comparables Calculator Drawing", { drawingType: tool, priceMode });
    };

    const clearDrawing = () => {
      const drawingId = getSelectedGeometry()?.id;
      drawingId && deleteDrawing(drawingId);
      setSelectedTool("");
      setDrawing(undefined);
    };

    const onUnitChange = async (unit: string) => {
      const isSmall = unit === unitOfMeasurement({ unitPreference, small: true });
      setSmallUnitSize(isSmall);
      setUnitSize(isSmall ? "small" : "large");
      logEvent("Comparables Calculator Unit", { unit, priceMode });
    };
    return (
      <div data-testid="select-area">
        <div className="atlas-flex atlas-justify-between atlas-items-center">
          <h4
            className={"atlas-m-0 atlas-text-base atlas-fit-content atlas-font-medium atlas-py-1"}
          >
            {t("comparables.calculator.drawingTools.title")}
            <DeprecatedTooltip
              className={"atlas-font-large atlas-px-1"}
              theme={Theme.Dark}
              placement="top"
              description={t("comparables.calculator.drawingTools.tooltip")}
            >
              <i className="icon-lt-question-line" />
            </DeprecatedTooltip>
          </h4>
          <div className="atlas-py-3 atlas-flex-end">
            <TextSwitch
              value={unitOfMeasurement({ unitPreference, small: smallUnitSize })}
              name="unitScaleSizeType"
              label={t("comparables.calculator.drawingTools.unitScale.label")}
              widthClass={"atlas-min-w-[150px]"}
              onChange={onUnitChange}
              options={[
                {
                  label: unitOfMeasurement({ unitPreference, small: true }),
                  value: unitOfMeasurement({ unitPreference, small: true }),
                },
                {
                  label: unitOfMeasurement({ unitPreference, small: false }),
                  value: unitOfMeasurement({ unitPreference, small: false }),
                },
              ]}
            />
          </div>
        </div>
        <div>
          <Tool
            onChange={handleSelectedToolChange}
            id="radius"
            isSelected={selectedTool === "ComparablesCircle"}
            label={t("comparables.calculator.drawingTools.radius.label")}
            value="ComparablesCircle"
          />
          {selectedTool === "ComparablesCircle" && (
            <RadiusInput
              isSmallScale={smallUnitSize}
              drawing={drawing}
              updateGeometry={(scale: number, drawing: AllGeoJSON) => {
                const newDrawing = transformScale(drawing, scale);
                updatedSelectedGeometry(newDrawing as DrawingItem);
              }}
              unitPreference={unitPreference}
              getRadius={(x) => polygonRadius(x as DrawingItem | null)}
            />
          )}
          <ComparablesCalculatorErrorMessage
            className="atlas-mt-2"
            drawing
            show={selectedTool === "ComparablesCircle" && drawing && isTooLarge}
          />
        </div>
        <div>
          <Tool
            onChange={handleSelectedToolChange}
            id="polygon"
            isSelected={selectedTool === "ComparablesPolygon"}
            label={t("comparables.calculator.drawingTools.polygon.label")}
            value="ComparablesPolygon"
          />
          <ComparablesCalculatorErrorMessage
            drawing
            show={selectedTool === "ComparablesPolygon" && drawing && isTooLarge}
          />
        </div>
        <div className={"atlas-flex atlas-items-center atlas-justify-center atlas-py-2"}>
          <Button.Secondary
            onClick={() => {
              clearDrawing();
            }}
            disabled={!drawing}
            theme={theme}
          >
            <i className="atlas-mr-1" />
            {t("comparables.calculator.drawingTools.clear")}
          </Button.Secondary>
        </div>
      </div>
    );
  }
);
